import { ChangeDetectionStrategy, Component, computed, inject, input, signal, ViewEncapsulation } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params } from '@angular/router';

import { IIFrameBlock } from './iframe.model';
import { DomSanitizer } from '@angular/platform-browser';
import { environment } from '../../../../../../environments/environment';
import { TailwindService } from '@seven1/angular-storyblok/tailwind';

@Component({
    selector: 'iframe[app-iframe]',
    imports: [],
    templateUrl: './iframe.component.html',
    styleUrl: './iframe.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'app-iframe',
        '[class]': 'classes()',
        '[src]': 'sanitizedLink()',
        '(load)': 'onLoad()',
        '[attr.sandbox]': 'sandbox()',
        allow: 'payment',
    },
})
export class IframeComponent {
    private _tailwind = inject(TailwindService);
    private _sanitizer = inject(DomSanitizer);
    private _route = inject(ActivatedRoute);
    private _queryParams = toSignal(this._route.queryParams, { initialValue: {} as Params });
    loading = signal(true);

    block = input.required<IIFrameBlock>();
    classes = computed(() => {
        const block = this.block();
        return [...this._tailwind.getSpacingClasses(block)];
    });
    private _isConfigurator = computed<boolean>(() => {
        const block = this.block();
        switch (block.category) {
            case 'general_configurator':
                return true;
            case 'dog':
                return block.option_dog === 'dog_configurator';
            case 'cat':
                return block.option_cat === 'cat_configurator';
            case 'horse':
                return block.option_horse === 'horse_configurator';
            default:
                return false;
        }
    });
    sandbox = computed<string | null>(() => {
        // Legacy (non-configurator) online applications must run with NO sandbox attribute at all,
        // otherwise their PayPal redirect flow breaks (per Barmenia). Returning null drops the attribute.
        // The configurator keeps a full sandbox including popups for the new PayPal flow.
        if (!this._isConfigurator()) return null;
        return 'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation allow-modals allow-downloads allow-storage-access-by-user-activation';
    });
    private _rawLink = computed<string | undefined>(() => {
        const blok = this.block();
        switch (blok.category) {
            case 'dog':
                return environment.iframes['dog'][blok.option_dog];
            case 'cat':
                return environment.iframes['cat'][blok.option_cat];
            case 'horse':
                return environment.iframes['horse'][blok.option_horse];
            case 'general':
                return environment.iframes['general'];
            case 'general_configurator':
                return environment.iframes['general_configurator'];
        }
    });
    sanitizedLink = computed(() => {
        const raw = this._rawLink();
        if (!raw) return undefined;
        const search = new URLSearchParams();
        for (const [key, value] of Object.entries(this._queryParams())) {
            if (key.startsWith('_storyblok')) continue;
            for (const entry of Array.isArray(value) ? value : [value]) {
                if (entry != null) search.append(key, entry);
            }
        }
        const query = search.toString();
        const url = query ? `${raw}${raw.includes('?') ? '&' : '?'}${query}` : raw;
        return this._sanitizer.bypassSecurityTrustResourceUrl(url);
    });

    onLoad(): void {
        this.loading.set(false);
    }
}
